import { useState } from 'react'
import type { PlanMember } from '../types'
import type { PatientContext } from '../utils/resolveMember'
import { IconPerson, IconPeople } from './Icons'

interface MemberSelectorProps {
  members: PlanMember[]
  patientContext: PatientContext | null
  planName: string
  payerName: string
  onSelect: (member: PlanMember) => void
  onBack: () => void
}

function formatDate(dateStr: string): string {
  if (!dateStr || !dateStr.includes('-')) return dateStr || '-'
  const [y, m, d] = dateStr.split('-')
  return `${m}/${d}/${y}`
}

function relationshipLabel(rel: PlanMember['relationship']): string {
  switch (rel) {
    case 'self': return 'Subscriber'
    case 'spouse': return 'Spouse'
    case 'child': return 'Child'
    case 'other': return 'Dependent'
  }
}

export default function MemberSelector({
  members,
  patientContext,
  planName,
  payerName,
  onSelect,
  onBack,
}: MemberSelectorProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

  function handleConfirm() {
    if (selectedIndex !== null && members[selectedIndex]) {
      onSelect(members[selectedIndex])
    }
  }

  return (
    <main className="dashboard-main">
      <div className="member-selector">
        <div className="member-selector__header">
          <div className="member-selector__icon">
            <IconPeople />
          </div>
          <div>
            <h2 className="member-selector__title">Select Plan Member</h2>
            <p className="member-selector__subtitle">
              Multiple members were found on this plan. Please confirm which member
              you are verifying coverage for.
            </p>
          </div>
        </div>

        {patientContext && (
          <div className="member-selector__context">
            <span className="member-selector__context-label">Searched for:</span>
            <span className="member-selector__context-value">
              {patientContext.firstName} {patientContext.lastName}
              {patientContext.dateOfBirth && ` \u00B7 DOB: ${formatDate(patientContext.dateOfBirth)}`}
            </span>
          </div>
        )}

        <div className="member-selector__plan-info">
          <span className="member-selector__plan-name">{planName}</span>
          <span className="member-selector__payer-name">{payerName}</span>
        </div>

        <div className="member-selector__list" role="radiogroup" aria-label="Plan members">
          {members.map((member, index) => (
            <label
              key={`${member.memberId}-${index}`}
              className={`member-selector__item ${
                selectedIndex === index ? 'member-selector__item--selected' : ''
              }`}
            >
              <input
                type="radio"
                name="plan-member"
                className="member-selector__radio"
                checked={selectedIndex === index}
                onChange={() => setSelectedIndex(index)}
              />
              <div className="member-selector__item-icon">
                {member.relationship === 'self' ? <IconPerson /> : <IconPeople />}
              </div>
              <div className="member-selector__item-info">
                <span className="member-selector__item-name">
                  {member.firstName} {member.lastName}
                </span>
                <span className="member-selector__item-meta">
                  {relationshipLabel(member.relationship)}
                  {member.dateOfBirth && ` \u00B7 DOB: ${formatDate(member.dateOfBirth)}`}
                  {member.memberId && ` \u00B7 ID: ${member.memberId}`}
                </span>
              </div>
              <span className={`member-selector__relationship-tag member-selector__relationship-tag--${member.relationship}`}>
                {relationshipLabel(member.relationship)}
              </span>
            </label>
          ))}
        </div>

        <div className="member-selector__actions">
          <button
            className="member-selector__back-btn"
            onClick={onBack}
          >
            Back
          </button>
          <button
            className="member-selector__confirm-btn"
            disabled={selectedIndex === null}
            onClick={handleConfirm}
          >
            Confirm &amp; View Benefits
          </button>
        </div>
      </div>
    </main>
  )
}
